import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton, Table, AV_FALLBACK, PageHead } from "@/v2/admin/ui";
import { useDept } from "./DeptShell";
import { Trophy, Flame, Building2, HandHeart } from "lucide-react";

const PERIODS = [["overall", "الإجمالي"], ["week", "هذا الأسبوع"], ["month", "هذا الشهر"]];

export default function DeptLeaderboard() {
  const { q } = useDept();
  const qc = useQueryClient();
  const [period, setPeriod] = useState("overall");
  const { data, isLoading } = useQuery({
    queryKey: ["dept-lb", q, period],
    queryFn: async () => (await api.get(`/dept/leaderboard?${q}&period=${period}`)).data,
  });

  const cheer = async (row) => {
    try {
      await api.post(`/dept/motivate?${q}`, {
        user_id: row.id, title: "تهنئة من مدير الإدارة",
        body: `👏 تهانينا ${row.name} على تقدّمك في لوحة متصدرين الإدارة — واصل التميز!`,
      });
      toast.success("تم إرسال التهنئة");
      qc.invalidateQueries({ queryKey: ["dept-lb"] });
    } catch (e) { toast.error(e.response?.data?.detail || "تعذّر الإرسال"); }
  };

  const rows = data?.rows || [];
  const top3 = rows.slice(0, 3);

  return (
    <div className="space-y-5" data-testid="dept-leaderboard">
      <PageHead icon={Trophy} title="متصدرو الإدارة" sub="ترتيب موظفي إدارتك ومكانة الإدارة بين بقية الإدارات.">
        <div className="flex gap-2">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`dept-lb-${k}`}
              className={`h-10 px-4 rounded-full text-xs font-bold ${period === k ? "bg-gradient-to-l from-[#e3c799] to-[#bb9661] text-[#25343f]" : "bg-white/5 border border-white/10 text-[#bcc8cf]"}`}>{l}</button>
          ))}
        </div>
      </PageHead>

      {data?.department_position && (
        <GlassCard gold className="p-5 flex items-center gap-4" data-testid="dept-position">
          <div className="w-12 h-12 rounded-2xl bg-[#bb9661]/15 text-[#e3c799] flex items-center justify-center shrink-0"><Building2 size={24} /></div>
          <div>
            <div className="font-extrabold">إدارة {data.department} في المركز <span className="v2-gold-text">{data.department_position}</span> من {data.departments_count} إدارة</div>
            <div className="text-xs text-[#bcc8cf] mt-0.5">الترتيب محسوب بمتوسط نقاط موظفي الإدارة</div>
          </div>
        </GlassCard>
      )}

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <>
          {top3.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-4">
              {top3.map((r) => (
                <GlassCard key={r.id} hover className="p-5 text-center" data-testid={`dept-podium-${r.rank}`}>
                  <div className="text-2xl font-extrabold v2-gold-text mb-2">#{r.rank}</div>
                  <img src={r.avatar} alt={r.name} onError={AV_FALLBACK} className="w-16 h-16 rounded-full object-cover mx-auto border-2 border-[#bb9661]/50" />
                  <div className="font-bold mt-2">{r.name}</div>
                  <div className="text-xs text-[#bcc8cf]">{r.level}</div>
                  <div className="text-lg font-extrabold v2-emerald-text mt-1">{r.score} نقطة</div>
                </GlassCard>
              ))}
            </div>
          )}

          <Table head={["#", "الموظف", "القسم", "المستوى", period === "overall" ? "النقاط" : "نقاط الفترة", "مكتمل", "متتالية", "الترتيب العام", ""]} min={900} testId="dept-lb-table">
            {rows.map((r) => (
              <tr key={r.id} data-testid="dept-lb-row">
                <td className="font-extrabold v2-gold-text">{r.rank}</td>
                <td>
                  <div className="flex items-center gap-3">
                    <img src={r.avatar} alt={r.name} onError={AV_FALLBACK} className="w-8 h-8 rounded-full object-cover" />
                    <span className="font-semibold">{r.name}</span>
                  </div>
                </td>
                <td className="text-[#bcc8cf]">{r.section || "—"}</td>
                <td><Chip tone="emerald">{r.level}</Chip></td>
                <td className="font-extrabold">{r.score}</td>
                <td>{r.completed}</td>
                <td><span className="inline-flex items-center gap-1"><Flame size={13} className="text-[#F7A08A]" /> {r.streak}</span></td>
                <td className="text-[#bcc8cf]">#{r.global_rank || "—"}</td>
                <td><button onClick={() => cheer(r)} data-testid={`dept-cheer-${r.rank}`} className="v2-btn-ghost v2-btn-sm"><HandHeart size={14} /> هنّئ</button></td>
              </tr>
            ))}
          </Table>
          {rows.length === 0 && <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا يوجد موظفون في هذه الإدارة بعد.</GlassCard>}
        </>
      )}
    </div>
  );
}
